// Link resolver — the linchpin of the shared navbar/footer.
//
// Links are stored SEMANTICALLY (a path + type), never pre-absolutized. Whether
// a link renders relative or absolute is a property of the CONSUMER's context,
// not of the link, so we resolve it here at render time:
//
//   - On the www family (www.postman.com, its *.www.postman-beta.com preview
//     branches, and local dev) internal links stay ROOT-RELATIVE so a click
//     keeps you on the current host (production, a preview branch, a locale).
//   - On any FOREIGN host (Learning Center, blog, support, or any other webapp)
//     internal links resolve ABSOLUTE to https://www.postman.com so they leave
//     that host correctly.
//
// External links carry their own host and pass through unchanged.

export const WWW_ORIGIN = 'https://www.postman.com';

// Locale URL prefixes.
//
// These MUST match `urlPrefix` in the marketing site's
// src/app/lib/i18n/config.js, which is the source of truth for what actually
// resolves. Two of the three are not the locale key:
//
//   ja     -> jp      (the JA site replaced the legacy Japanese property)
//   fr-CA  -> fr-ca   (lowercased, hyphenated — NOT `fr`)
//
// `fr-CA` was `fr` here from the Phase 0 scaffold until v1.0.3, which pointed
// all 37 links in the fr-CA navbar at non-existent /fr/ pages. Nothing caught
// it because the only prefix assertion in the suite covered /jp/. There is now
// a case per locale in test/resolve-href.test.js — keep them in sync with the
// consumer's config rather than adding a prefix here on inference.
const LOCALE_PREFIX = { ja: 'jp', es: 'es', 'fr-CA': 'fr-ca' };

/**
 * Locales whose URLs get a prefix. Deliberately independent of which locales
 * have a translated COPY bundle: a locale can localize its URLs while its copy
 * still falls back to English, so the prerenderer must emit a fragment for
 * every locale here — not just the translated ones.
 */
export const URL_LOCALES = Object.keys(LOCALE_PREFIX);

// prefix -> locale, e.g. 'jp' -> 'ja'. Two of the three prefixes are not the
// locale key, so this cannot be derived by guessing.
const PREFIX_TO_LOCALE = Object.fromEntries(
  Object.entries(LOCALE_PREFIX).map(([locale, prefix]) => [prefix, locale])
);

/**
 * Derive the locale from a URL path, e.g. '/jp/pricing/' -> 'ja'.
 *
 * For hosts with no router to ask. Fern's custom-header compiler forbids all
 * imports and exposes no usePathname, so the Learning Center has to parse the
 * URL by hand; doing it here means every such consumer gets the same answer
 * instead of each re-deriving the prefix map (and getting fr-CA wrong, as this
 * repo itself did until v1.0.3).
 *
 * @param {string} [pathname] Defaults to location.pathname when in a browser.
 * @returns {string|null} A locale key, or null when the path carries no known prefix.
 */
export function localeFromPathname(pathname) {
  const path = pathname ?? (typeof location !== 'undefined' ? location.pathname : undefined);
  if (!path) return null;
  const first = path.split('/').filter(Boolean)[0];
  return first ? (PREFIX_TO_LOCALE[first.toLowerCase()] ?? null) : null;
}

/**
 * The CURRENT page's path, re-pointed at another locale — '/pricing/' + 'ja'
 * gives '/jp/pricing/'. For the footer's language switcher, where the useful
 * destination is this page in the other language, not the other language's
 * homepage.
 *
 * Mirrors the marketing site's getCleanPath + getLocaleUrl (src/app/lib/i18n/
 * url-utils.js): strip any locale prefix the path already carries, then apply
 * the target locale's. Keep the two in step.
 *
 * Prefixes UNCONDITIONALLY, without consulting whether the page is actually
 * translated — deliberately, because www's switcher does the same. An
 * untranslated /jp/<path>/ is server-redirected back to its EN form, a
 * double-hop the user can see in the URL bar; www accepts it here (though NOT
 * for ordinary chrome links, which it routes through localizeUrl) because a
 * switcher that silently kept you on the EN page would look broken. The
 * component cannot tell the difference anyway: it has no translated-page
 * manifest, and inventing one would put it out of step with the consumer's.
 *
 * @param {string} targetLocale - locale key to switch to ('en', 'ja', …)
 * @param {string} [pathname] Defaults to location.pathname when in a browser.
 * @returns {string} A root-relative path, always leading-slashed.
 */
export function localeSwitchPath(targetLocale, pathname) {
  const path = pathname ?? (typeof location !== 'undefined' ? location.pathname : '/');
  const segments = (path || '/').replace(/^\//, '').split('/');
  const first = segments[0]?.toLowerCase();
  const clean = PREFIX_TO_LOCALE[first] ? segments.slice(1) : segments;
  const prefix = LOCALE_PREFIX[targetLocale];
  const rest = clean.join('/');
  return prefix && targetLocale !== 'en' ? `/${prefix}/${rest}` : `/${rest}`;
}

/**
 * Is `origin` part of the www family (where internal links stay root-relative)?
 * @param {string} origin - e.g. 'https://mybranch.www.postman-beta.com'
 * @returns {boolean}
 */
export function isWwwFamily(origin) {
  if (!origin) return false;
  try {
    const { hostname } = new URL(origin);
    return (
      hostname === 'www.postman.com' ||
      hostname === 'www.postman-beta.com' ||
      hostname.endsWith('.www.postman-beta.com') ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1'
    );
  } catch {
    return false;
  }
}

function localizePath(path, locale) {
  const prefix = LOCALE_PREFIX[locale];
  if (!prefix || locale === 'en') return path;
  return `/${prefix}${path}`;
}

/**
 * Resolve a semantic link descriptor to an href for the given context.
 *
 * @param {object} link - { type: 'internal'|'external'|'anchor', path?, url?, hash?, i18n? }
 * @param {object} [ctx] - { origin?, locale?, localizesPages? }
 * @returns {string}
 */
export function resolveHref(link, ctx = {}) {
  if (!link) return '';

  // External: host is part of the link's identity — never rewrite it.
  if (link.type === 'external') return link.url ?? '';

  const origin = ctx.origin ?? (typeof location !== 'undefined' ? location.origin : '');
  const onWww = isWwwFamily(origin);
  const base = onWww ? '' : WWW_ORIGIN;

  if (link.type === 'anchor') {
    const path = link.path ?? '/';
    return `${base}${path}${link.hash ?? ''}`;
  }

  // internal
  let path = link.path ?? '/';
  if (link.i18n && ctx.localizesPages && ctx.locale) {
    path = localizePath(path, ctx.locale);
  }
  return `${base}${path}`;
}
